import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import MDEditor from '@uiw/react-md-editor';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Label } from "@/components/ui/label";

export default function Templates() {
  const { toast } = useToast();
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<null | {
    template_id: number;
    name: string;
    content: string;
    type: "certificate";
    variable_format?: Record<string, { prefix: string; suffix: string }>;
    artwork_preview_settings?: {
      width: string;
      alignment: "left" | "center" | "right";
    };
  }>(null);

  const defaultVariables = {
    artist_name: { prefix: "Artist: ", suffix: "" },
    artwork_title: { prefix: "Title: ", suffix: "" },
    creation_date: { prefix: "Created: ", suffix: "" },
    art_technique: { prefix: "Technique: ", suffix: "" },
    width: { prefix: "Width: ", suffix: "" },
    height: { prefix: "Height: ", suffix: "" },
    issue_date: { prefix: "Issue Date: ", suffix: "" },
    print_number: { prefix: "Print Number: ", suffix: "" },
    media: { prefix: "Media: ", suffix: "" },
    print_provider: { prefix: "Print Provider: ", suffix: "" },
    hologram_number: { prefix: "Hologram Number: ", suffix: "" },
    artwork_preview: { prefix: "", suffix: "" },
  };

  const { data: templatesList, isLoading, refetch } = useQuery({
    queryKey: ["templates-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching templates:", error);
        throw error;
      }

      return data;
    },
  });

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;

    if (selectedTemplate.template_id) {
      // Update existing template
      const { error } = await supabase
        .from("templates")
        .update({
          name: selectedTemplate.name,
          content: selectedTemplate.content,
          variable_format: selectedTemplate.variable_format || defaultVariables,
          artwork_preview_settings: selectedTemplate.artwork_preview_settings || {
            width: "300",
            alignment: "center"
          },
        })
        .eq("template_id", selectedTemplate.template_id);

      if (error) {
        console.error("Error updating template:", error);
        toast({
          title: "Error",
          description: "Failed to update template",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Template updated successfully",
      });
    } else {
      // Create new template
      const { error } = await supabase
        .from("templates")
        .insert([{
          name: selectedTemplate.name,
          content: selectedTemplate.content,
          type: "certificate",
          variable_format: selectedTemplate.variable_format || defaultVariables,
          artwork_preview_settings: selectedTemplate.artwork_preview_settings || {
            width: "300",
            alignment: "center"
          },
        }]);

      if (error) {
        console.error("Error creating template:", error);
        toast({
          title: "Error",
          description: "Failed to create template",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Template created successfully",
      });
    }

    setIsTemplateDialogOpen(false);
    setSelectedTemplate(null);
    refetch();
  };

  const handleSetDefault = async (templateId: number) => {
    const { error: updateError } = await supabase
      .from("templates")
      .update({ is_default: false })
      .eq("type", "certificate");

    if (updateError) {
      console.error("Error updating templates:", updateError);
      return;
    }

    const { error } = await supabase
      .from("templates")
      .update({ is_default: true })
      .eq("template_id", templateId);

    if (error) {
      console.error("Error setting default template:", error);
      toast({
        title: "Error",
        description: "Failed to set default template",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Default template updated successfully",
    });
    refetch();
  };

  const handleEditTemplate = (template: any) => {
    setSelectedTemplate({
      ...template,
      variable_format: template.variable_format || defaultVariables,
      artwork_preview_settings: template.artwork_preview_settings || {
        width: "300",
        alignment: "center"
      },
    });
    setIsTemplateDialogOpen(true);
  };

  const handleCreateNewTemplate = () => {
    setSelectedTemplate({
      template_id: 0,
      name: "",
      content: "",
      type: "certificate",
      variable_format: defaultVariables,
      artwork_preview_settings: {
        width: "300",
        alignment: "center"
      },
    });
    setIsTemplateDialogOpen(true);
  };

  const handleFormatChange = (variable: string, type: 'prefix' | 'suffix', value: string) => {
    if (!selectedTemplate) return;
    
    setSelectedTemplate(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        variable_format: {
          ...prev.variable_format,
          [variable]: {
            ...prev.variable_format?.[variable],
            [type]: value
          }
        }
      };
    });
  };

  const handleArtworkPreviewSettingChange = (setting: 'width' | 'alignment', value: string) => {
    if (!selectedTemplate) return;
    
    setSelectedTemplate(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        artwork_preview_settings: {
          ...prev.artwork_preview_settings,
          [setting]: value
        }
      };
    });
  };

  // Sort variables alphabetically for consistent display
  const sortedVariables = Object.entries(defaultVariables)
    .filter(([key]) => key !== 'artwork_preview') // Filter out artwork_preview as we'll handle it separately
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Certificate Templates</h1>
          <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleCreateNewTemplate}>
                <PlusCircle className="mr-2" />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedTemplate?.template_id ? 'Edit Template' : 'Create New Template'}</DialogTitle>
                <DialogDescription>
                  Create a new certificate template using Markdown. You can use variables like {`{hologram_number}`}, {`{issue_date}`}, and {`{artwork_preview}`} to include artwork image.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={selectedTemplate?.name || ""}
                    onChange={(e) =>
                      setSelectedTemplate(prev => prev ? { ...prev, name: e.target.value } : null)
                    }
                  />
                </div>
                <div>
                  <Label>Content</Label>
                  <div data-color-mode="light">
                    <MDEditor
                      value={selectedTemplate?.content || ""}
                      onChange={(value) =>
                        setSelectedTemplate(prev => prev ? { ...prev, content: value || "" } : null)
                      }
                      preview="edit"
                      height={400}
                    />
                  </div>
                </div>
                <div className="p-4 border rounded-md bg-amber-50">
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>Tip:</strong> Use {`{artwork_preview}`} to include the artwork image in your certificate. 
                    You can customize its appearance using the settings below.
                  </p>
                </div>
                
                {/* Artwork Preview Settings */}
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h3 className="font-medium mb-3">Artwork Preview Settings</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="artwork-width">Width (px)</Label>
                        <Input
                          id="artwork-width"
                          type="number"
                          value={selectedTemplate?.artwork_preview_settings?.width || "300"}
                          min="50"
                          max="800"
                          onChange={(e) => handleArtworkPreviewSettingChange('width', e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Label htmlFor="artwork-alignment">Alignment</Label>
                        <select
                          id="artwork-alignment"
                          value={selectedTemplate?.artwork_preview_settings?.alignment || "center"}
                          onChange={(e) => handleArtworkPreviewSettingChange('alignment', e.target.value as 'left' | 'center' | 'right')}
                          className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Other Variable Format Settings */}
                <div className="space-y-4">
                  <Label className="block mb-4">Variable Formatting</Label>
                  <div className="space-y-3 border rounded-lg p-4">
                    {sortedVariables.map(([variable, _]) => (
                      <div key={variable} className="grid grid-cols-[1fr,2fr,auto,2fr] gap-2 items-center">
                        <Label className="font-medium capitalize">
                          {variable.replace(/_/g, ' ')}
                        </Label>
                        <Input
                          placeholder="Prefix (before)"
                          value={selectedTemplate?.variable_format?.[variable]?.prefix || ''}
                          onChange={(e) => handleFormatChange(variable, 'prefix', e.target.value)}
                          className="w-full"
                        />
                        <div className="text-center text-sm text-muted-foreground px-2">
                          {`{${variable}}`}
                        </div>
                        <Input
                          placeholder="Suffix (after)"
                          value={selectedTemplate?.variable_format?.[variable]?.suffix || ''}
                          onChange={(e) => handleFormatChange(variable, 'suffix', e.target.value)}
                          className="w-full"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={handleSaveTemplate}>
                  {selectedTemplate?.template_id ? 'Update Template' : 'Create Template'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div>Loading templates...</div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templatesList?.map((template) => (
                  <TableRow 
                    key={template.template_id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleEditTemplate(template)}
                  >
                    <TableCell>{template.name}</TableCell>
                    <TableCell>{template.type}</TableCell>
                    <TableCell>{template.is_default ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      {new Date(template.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {!template.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(template.template_id)}
                        >
                          Set as Default
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
